import React from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Link,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { getStripeInvoices, getBillingAttention } from "./api";
import { StripeInvoice, StripeInvoicePage } from "./types";

const PAGE_SIZE = 10;

const EMPTY_INVOICE_PAGE: StripeInvoicePage = {
  data: [],
  meta: { has_more: false, next_cursor: null },
};

const formatInvoiceDate = (date: string) =>
  new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const formatInvoiceAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);

const formatStatus = (status: string | null) =>
  status === "open"
    ? "Unpaid"
    : status
    ? status.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
    : "Unknown";

const statusColor = (status: string | null) => {
  switch (status) {
    case "paid":
      return "green";
    case "open":
      return "yellow";
    case "void":
      return "gray";
    case "uncollectible":
      return "red";
    default:
      return "gray";
  }
};

const BillingHistoryEmptyState = ({ mutedText }: { mutedText: string }) => (
  <VStack spacing={2} py={10} px={6} textAlign="center">
    <Text fontSize="sm" fontWeight="600">
      No invoices yet
    </Text>
    <Text fontSize="sm" color={mutedText}>
      Invoices will appear after the first billing event.
    </Text>
  </VStack>
);

export const BillingHistory = ({
  stripeCustomerId,
}: {
  stripeCustomerId: string | null;
}) => {
  const paymentGeneration = React.useRef(0);
  const [pages, setPages] = React.useState<StripeInvoicePage[]>([]);
  const [pageIndex, setPageIndex] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [paying, setPaying] = React.useState<string | null>(null);
  const [paymentNotice, setPaymentNotice] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const sectionHeaderColor = useColorModeValue("gray.700", "gray.300");
  const dividerBorderColor = useColorModeValue("gray.200", "gray.700");
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const tableHeaderBg = useColorModeValue("gray.50", "gray.700");

  React.useEffect(() => {
    setPaymentNotice(null);
    setPaying(null);
    return () => {
      paymentGeneration.current += 1;
    };
  }, [stripeCustomerId]);

  React.useEffect(() => {
    let isMounted = true;
    setPages([]);
    setPageIndex(0);
    setError(null);

    if (!stripeCustomerId) {
      setIsLoading(false);
      setPages([EMPTY_INVOICE_PAGE]);
      return () => {
        isMounted = false;
      };
    }

    setIsLoading(true);
    getStripeInvoices({ limit: PAGE_SIZE })
      .then((page) => {
        if (!isMounted) return;
        setPages([page]);
      })
      .catch(() => {
        if (!isMounted) return;
        setError("Unable to load invoices.");
      })
      .finally(() => {
        if (!isMounted) return;
        setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [stripeCustomerId]);

  const currentPage = pages[pageIndex] || EMPTY_INVOICE_PAGE;
  const isInitialLoading = isLoading && pages.length === 0;

  const onNext = async () => {
    if (!currentPage.meta.next_cursor) return;
    const nextIndex = pageIndex + 1;

    if (pages[nextIndex]) {
      setPageIndex(nextIndex);
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      const nextPage = await getStripeInvoices({
        limit: PAGE_SIZE,
        cursor: currentPage.meta.next_cursor,
      });
      setPages((existingPages) => [...existingPages, nextPage]);
      setPageIndex(nextIndex);
    } catch {
      setError("Unable to load invoices.");
    } finally {
      setIsLoading(false);
    }
  };

  const pay = async (url: string) => {
    const generation = paymentGeneration.current;
    setPaying(url);
    setPaymentNotice(null);
    try {
      const fresh = await getBillingAttention();
      if (generation !== paymentGeneration.current) return;
      const match = fresh
        .flatMap((row) => row.invoices)
        .find((invoice) => invoice.url === url);
      if (match?.url) {
        window.location.assign(match.url);
      } else {
        // Cached pages must not keep offering an invoice rejected by fresh checks.
        setPages((existing) =>
          existing.map((page) => ({
            ...page,
            data: page.data.map((invoice) =>
              invoice.url === url
                ? { ...invoice, url: null, action: null }
                : invoice
            ),
          }))
        );
        setPaymentNotice(
          "This invoice is no longer available for payment. Refresh billing details."
        );
      }
    } catch {
      if (generation !== paymentGeneration.current) return;
      setPaymentNotice("Billing details are unavailable. Please try again.");
    } finally {
      if (generation === paymentGeneration.current) setPaying(null);
    }
  };

  const servicePeriod = (invoice: StripeInvoice) => {
    if (!invoice.period_start || !invoice.period_end) return "—";
    const date = (value: string) =>
      new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
    return `${date(invoice.period_start)} – ${date(invoice.period_end)}`;
  };

  const onPrevious = () => {
    setPageIndex((index) => Math.max(index - 1, 0));
  };

  return (
    <Box>
      <HStack mb={3}>
        <Heading size="sm" color={sectionHeaderColor} fontWeight="600">
          Billing History
        </Heading>
      </HStack>
      <Divider borderColor={dividerBorderColor} mb={5} />
      {paymentNotice && (
        <Text role="alert" fontSize="sm" mb={3}>
          {paymentNotice}
        </Text>
      )}

      <Box
        bg={cardBg}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={cardBorder}
        overflow="hidden"
      >
        {!stripeCustomerId ? (
          <BillingHistoryEmptyState mutedText={mutedText} />
        ) : isInitialLoading ? (
          <Flex minH="180px" align="center" justify="center">
            <Spinner color="brand.400" />
          </Flex>
        ) : error ? (
          <VStack spacing={3} py={10} px={6} textAlign="center">
            <Text fontSize="sm" color="red.300">
              {error}
            </Text>
            {stripeCustomerId && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setPages([]);
                  setPageIndex(0);
                  setError(null);
                  setIsLoading(true);
                  getStripeInvoices({ limit: PAGE_SIZE })
                    .then((page) => setPages([page]))
                    .catch(() => setError("Unable to load invoices."))
                    .finally(() => setIsLoading(false));
                }}
              >
                Retry
              </Button>
            )}
          </VStack>
        ) : currentPage.data.length === 0 ? (
          <BillingHistoryEmptyState mutedText={mutedText} />
        ) : (
          <>
            <Box overflowX="auto">
              <Table size="sm" minW="640px">
                <Thead bg={tableHeaderBg}>
                  <Tr>
                    <Th>Date</Th>
                    <Th>Invoice</Th>
                    <Th>Service period</Th>
                    <Th>Status</Th>
                    <Th isNumeric>Amount</Th>
                    <Th textAlign="right">Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {currentPage.data.map((invoice: StripeInvoice, index) => (
                    <Tr
                      key={`${invoice.invoice_number || "pending"}-${
                        invoice.date
                      }-${index}`}
                    >
                      <Td fontSize="sm">{formatInvoiceDate(invoice.date)}</Td>
                      <Td fontSize="sm" fontWeight="500">
                        {invoice.invoice_number || "Pending invoice"}
                      </Td>
                      <Td fontSize="sm">{servicePeriod(invoice)}</Td>
                      <Td>
                        <Badge colorScheme={statusColor(invoice.status)}>
                          {invoice.status === "open" &&
                          invoice.payment_processing
                            ? "Processing payment"
                            : formatStatus(invoice.status)}
                        </Badge>
                      </Td>
                      <Td isNumeric fontSize="sm">
                        {formatInvoiceAmount(invoice.amount, invoice.currency)}
                      </Td>
                      <Td textAlign="right">
                        {invoice.url &&
                        invoice.action === "pay" &&
                        invoice.status === "open" &&
                        !invoice.payment_processing ? (
                          <Button
                            size="sm"
                            variant="link"
                            color="accent"
                            isLoading={paying === invoice.url}
                            isDisabled={paying !== null}
                            onClick={() => pay(invoice.url!)}
                          >
                            Pay invoice
                          </Button>
                        ) : invoice.url &&
                          ["paid", "void"].includes(invoice.status || "") ? (
                          <Link
                            href={invoice.url}
                            isExternal
                            color="accent"
                            fontSize="sm"
                            whiteSpace="nowrap"
                          >
                            View invoice{" "}
                            <ExternalLinkIcon mx="2px" boxSize={3} />
                          </Link>
                        ) : (
                          <Text fontSize="sm" color={mutedText}>
                            —
                          </Text>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            <Divider borderColor={dividerBorderColor} />
            <Flex
              px={6}
              py={4}
              align={{ base: "stretch", sm: "center" }}
              justify="space-between"
              gap={3}
              direction={{ base: "column", sm: "row" }}
            >
              <Text fontSize="sm" color={mutedText}>
                Page {pageIndex + 1}
              </Text>
              <HStack>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onPrevious}
                  isDisabled={pageIndex === 0 || isLoading}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  colorScheme="brand"
                  onClick={onNext}
                  isLoading={isLoading}
                  isDisabled={!currentPage.meta.next_cursor}
                >
                  Next
                </Button>
              </HStack>
            </Flex>
          </>
        )}
      </Box>
    </Box>
  );
};
